import { api } from './api'
import { CarePlan, CarePlanFormData, CarePlanSuggestion } from '../types/carePlan'

export const carePlanService = {
  /**
   * Get all care plans for a patient
   */
  getAll: async (patientId: string): Promise<CarePlan[]> => {
    const response = await api.get<CarePlan[]>(`/patients/${patientId}/care-plans`)
    return response.data
  },

  /**
   * Get a single care plan by ID
   */
  getById: async (id: string): Promise<CarePlan> => {
    const response = await api.get<CarePlan>(`/care-plans/${id}`)
    return response.data
  },

  /**
   * Create a new care plan
   */
  create: async (data: CarePlanFormData): Promise<CarePlan> => {
    const response = await api.post<CarePlan>(`/patients/${data.patientId}/care-plans`, data)
    return response.data
  },

  /**
   * Update an existing care plan
   */
  update: async (id: string, data: Partial<CarePlanFormData>): Promise<CarePlan> => {
    const response = await api.put<CarePlan>(`/care-plans/${id}`, data)
    return response.data
  },

  /**
   * Delete a care plan
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/care-plans/${id}`)
  },

  /**
   * Get context-aware care plan suggestions for a patient
   */
  getSuggestions: async (patientId: string): Promise<CarePlanSuggestion[]> => {
    const response = await api.get<CarePlanSuggestion[]>(`/patients/${patientId}/care-plans/suggestions`)
    return response.data
  },

  /**
   * Get all care plan templates
   */
  getTemplates: async (category?: string): Promise<CarePlan[]> => {
    const params = category ? { category } : {}
    const response = await api.get<CarePlan[]>('/care-plans/templates', { params })
    return response.data
  },

  /**
   * Get a single template by ID
   */
  getTemplate: async (id: string): Promise<CarePlan> => {
    const response = await api.get<CarePlan>(`/care-plans/templates/${id}`)
    return response.data
  },

  /**
   * Create a template from a care plan
   */
  createTemplate: async (data: CarePlanFormData): Promise<CarePlan> => {
    const response = await api.post<CarePlan>('/care-plans/templates', { ...data, isTemplate: true })
    return response.data
  },

  /**
   * Create a care plan from a template
   */
  createFromTemplate: async (patientId: string, templateId: string, data?: Partial<CarePlanFormData>): Promise<CarePlan> => {
    const response = await api.post<CarePlan>(`/patients/${patientId}/care-plans/from-template/${templateId}`, data || {})
    return response.data
  },

  /**
   * Get care plan history/audit trail
   */
  getHistory: async (id: string): Promise<any[]> => {
    const response = await api.get<any[]>(`/care-plans/${id}/history`)
    return response.data
  },

  /**
   * Transfer care plan to another provider
   */
  transfer: async (id: string, targetProviderId: string, reason?: string): Promise<CarePlan> => {
    const response = await api.post<CarePlan>(`/care-plans/${id}/transfer`, { targetProviderId, reason })
    return response.data
  },
}
